import React from 'react'
import { connect } from 'react-redux'
import { ConfirmationDialog } from 'common/styleguide/ConfirmationDialog'
import styles from './CopyToRoutesModal.scss'
import { Icon } from 'common/Icon'
import { Loadable } from 'common/Loadable'
import fetchJSON from 'lib/fetchJSON'
import { FancyCheckmark } from 'common/FancyCheckmark'
import { errorMessage } from 'lib/styleguide/base.scss'
import { getConsole } from 'lib/getConsole'
import { getCurrentUser, getBaseCurrentUser } from 'lib/utils'
import { urls } from 'lib/urls'
import { Msg, fm, commonNouns, commonMessages, commonActions, privacyLevels, commonCTAs } from 'lib/localization'
import { srMessages } from 'features/recumbent/SaveRoute'
import { organizedMessages } from 'features/recumbent/SaveRoute/SaveSuccess'
import { clubActions, clubSelectors } from 'data/resources/clubs'
import { FormattedMessage as T, defineMessages, injectIntl } from 'react-intl'
import _ from 'lodash'
const console = getConsole('routes', 'CopyToRoutesModal')

export const copyMessages = defineMessages({
  viewMyCopy: {
    id: 'copyMessages.viewMyCopy',
    defaultMessage: 'View My Copy'
  },
  pinnedTip: {
    id: 'copyMessages.pinnedTip',
    defaultMessage: 'You can find Pinned Routes on your homepage, and in the Pinned section of the app'
  },
  copying: {
    id: 'copyMessages.copying',
    defaultMessage: 'copying'
  },
  theRoute: {
    id: 'copyMessages.theRoute',
    defaultMessage: 'the route'
  },
  unnamed: {
    id: 'copyMessages.unnamed',
    defaultMessage: 'unnamed'
  }
})

export const COPY_TO_ROUTES_MODAL = 'COPY_TO_ROUTES_MODAL'

class _CopyToRoutesModal extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      visibility: _.get(getCurrentUser(), 'preferences.default_privacy_route', 1),
      userId: props.userId,
      name: `${props.troute.name} (${fm(props.intl, commonActions.copy)})`,
      includePhotos: false,
      copyPending: false,
      copyResult: null,
      copyError: null
    }
  }

  onUserIdChange = (e) => {
    this.setState({ userId: parseInt(e.target.value, 10) })
  }

  onVisibilityChange = (e) => {
    this.setState({ visibility: parseInt(e.target.value, 10) })
  }

  onNameChange = (e) => {
    this.setState({ name: e.target.value })
  }

  onCopyPhotosChange = () => {
    this.setState({ includePhotos: !this.state.includePhotos })
  }

  onSubmitForm = (e) => {
    e.preventDefault()
    this.onCopyConfirmed()
  }

  viewCopy = (e) => {
    const { copyResult } = this.state
    if (copyResult) {
      window.location = copyResult.url
    } else {
      console.error('viewCopy called without a successful copy result')
    }
  }

  onCopyConfirmed = () => {
    const { troute, objectType } = this.props
    const { name, userId, visibility, includePhotos, copyPending, copyResult } = this.state
    if (copyPending || copyResult) { return }
    const params = {
      user_id: userId,
      asset_type: objectType,
      privacy_code: troute.privacyCode,
      name,
      visibility,
      include_photos: this.canCopyPhotos(troute) && includePhotos
    }
    // the copy.json endpoint doesn't return the created route or even the
    // route ID, so it doesn't make sense to add it to lib/api/routes yet
    // (we have to do a full page navigation anyways)
    this.setState({ copyPending: true })
    // DESPITE this sometimes being a trip ID and not a route ID, we still want
    // to use 'routes' in the URL.
    fetchJSON(`/routes/${troute.id}/copy.json`, params)
      .then(copyResult => this.setState({ copyResult, copyError: null, copyPending: false }))
      .catch(copyError => this.setState({ copyError, copyResult: null, copyPending: false }))
  }

  canCopyPhotos = (troute) => (
    troute.userId === getCurrentUser().id || (getBaseCurrentUser() && troute.userId === getBaseCurrentUser().id)
  )

  render () {
    const { currentUserId, onRequestClose, troute, intl, loadableResources: { clubs }, clubByUserId } = this.props
    const { userId, visibility, name, includePhotos, copyResult, copyError, copyPending } = this.state
    const copyToClub = currentUserId !== userId
    const confirmText = copyResult
      ? (copyToClub ? <Msg m={organizedMessages.view} values={{ asset: _.capitalize(fm(intl, commonNouns.routes, { count: 1 })) }} /> : fm(intl, copyMessages.viewMyCopy))
      : (copyToClub ? _.capitalize(fm(intl, commonActions.copy)) : fm(intl, commonActions.copyToMyRoutes))

    return (
      <ConfirmationDialog
        title={<T id='CopyToRoutesModal.title'
          defaultMessage='Copy <strong>{name}</strong>'
          values={{ strong: (...c) => <strong>{c}</strong>, name: troute.name }} />}
        confirmText={confirmText}
        cancelText={copyResult && <Msg m={commonCTAs.close} />}
        confirmPending={copyPending}
        onConfirm={copyResult ? this.viewCopy : this.onCopyConfirmed}
        onRequestClose={onRequestClose}
        onCancel={onRequestClose}
        maxWidth='550px'>
        <div className={styles.CopyToRoutesModal}>
          {!copyResult && <>
            <form onSubmit={this.onSubmitForm}>
              <T id='CopyToRoutesModal.routeName' defaultMessage='Route Name' />
              <input
                id='route-name'
                name='route-name'
                type='text'
                value={name}
                onChange={this.onNameChange} />
              {clubs.length > 0 && <>
                <label htmlFor='copy-to'><Msg m={srMessages.account} /></label>
                <select
                  id='user-id'
                  name='user-id'
                  value={userId}
                  onChange={this.onUserIdChange}>
                  <option value='0'>{fm(intl, srMessages.personal)}</option>
                  {clubs.map(c => <option key={c.id} value={c.userId}>{c.name}</option>)}
                </select>
              </>}
              <label htmlFor='route-visiblity'><Msg m={privacyLevels.privacy} /></label>
              <select
                id='route-visibility'
                name='route-visibility'
                value={visibility}
                onChange={this.onVisibilityChange}>
                <option value='0'>{fm(intl, privacyLevels.public)}</option>
                <option value='1'>{fm(intl, privacyLevels.private)}</option>
                <option value='2'>{fm(intl, privacyLevels.friendsOnly)}</option>
              </select>
              {this.canCopyPhotos(troute) && <div className={styles.inline}>
                <input id='copy-photos'
                  name='copy-photos'
                  type='checkbox'
                  value={includePhotos}
                  onChange={this.onCopyPhotosChange} />
                <label className={styles.labelInline} htmlFor='copy-photos'>
                  <T id='CopyToRoutesModal.copyPhotos' defaultMessage='Copy photos' />
                </label>
              </div>}
            </form>
            <p>
              <T
                id='CopyToRoutesModal.editNotRequired'
                defaultMessage="Don't need to edit this route? Try adding it to your <a>Pinned Routes</a> instead!"
                values={{
                  a: (...c) => <a href='https://ridewithgps.com/help/pinning-and-sharing#pinning' target='_blank'>{c}</a>,
                  icon: <Icon className={styles.helpIcon} i='help-circled' data-tip title={fm(intl, copyMessages.pinnedTip)} />
                }} />
            </p>
          </>}
          {copyError && <div className={errorMessage}>
            <Msg
              m={commonMessages.errorMessage}
              values={{
                action: fm(intl, copyMessages.copying),
                operand: fm(intl, copyMessages.theRoute)
              }} />
          </div>}
          {copyResult && <div style={{ textAlign: copyToClub ? 'center' : undefined }}>
            <FancyCheckmark />
            {copyToClub
              ? <Msg
                m={organizedMessages.savedTo}
                values={{
                  strong: (...c) => <strong>{c}</strong>,
                  name: troute.name,
                  location: <Msg
                    m={organizedMessages.orgLib}
                    values={{
                      a: (...c) => <a href={urls.organizations.routes(clubByUserId(userId).id)}>{c}</a>,
                      name: clubByUserId(userId).name
                    }}
                  />
                }} />
              : <T
                id='CopyToRoutesModal.success'
                defaultMessage='<strong>{routeName}</strong> has been copied to your routes. Would you like to <a>edit it in the Route Planner</a>?'
                values={{
                  strong: (...c) => <strong>{c}</strong>,
                  a: (...c) => <a href={copyResult.url + '/edit'}>{c}</a>,
                  routeName: name ?? `(${fm(intl, copyMessages.unnamed)})`
                }} />}
            {includePhotos && <div>
              <br />
              <T id='CopyToRoutesModal.photosCopying' defaultMessage='Your photos are being copied over, they will show up in a moment.' />
            </div>}
          </div>}
        </div>
      </ConfirmationDialog>
    )
  }
}

class LoadableCopyToRoutesModal extends React.PureComponent {
  render () {
    const userId = getCurrentUser().id
    return <Loadable
      {...this.props}
      userId={userId}
      currentUserId={userId}
      component={_CopyToRoutesModal}
      resourceLoaded={() => this.props.loadClubs(userId).then(r => ({ clubs: r.value.results }))} />
  }
}

export const CopyToRoutesModal = connect(
  state => ({ clubByUserId: id => clubSelectors.byUserId(state, id) }),
  dispatch => ({ loadClubs: userId => dispatch(clubActions.privilegedClubsForUser({ userId, managesRoutes: true, admin: true })) })
)(injectIntl(LoadableCopyToRoutesModal))
